import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/hooks/ui/use-toast";
import Navbar from "@/components/layout/Navbar";
import QRCode from "react-qr-code";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Download, 
  Plus, 
  QrCode, 
  Edit, 
  Building, 
  Phone, 
  Globe, 
  MapPin 
} from "lucide-react";

type Business = {
  id: string;
  name: string;
  description: string | null;
  address: string;
  website: string | null;
  phone: string | null;
  created_at: string;
  updated_at: string | null; // Make updated_at optional as it might not always be present or fetched
};

type QrCodeData = {
  id: string;
  name: string;
  unique_url_path: string;
  created_at: string;
};

const BusinessDetails = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [business, setBusiness] = useState<Business | null>(null);
  const [qrCodes, setQrCodes] = useState<QrCodeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [newQrName, setNewQrName] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      toast.error("You must be logged in to view business details", { businessName: "System" });
      navigate("/sign-in");
      return;
    }

    const fetchBusinessDetails = async () => {
      try {
        // Fetch business details
        const { data: businessData, error: businessError } = await supabase
          .from("businesses")
          .select("*")
          .eq("id", businessId)
          .single();

        if (businessError) throw businessError;
        setBusiness(businessData as Business);

        // Fetch QR codes for this business
        const { data: qrData, error: qrError } = await supabase
          .from("qr_codes")
          .select("*")
          .eq("business_id", businessId)
          .order("created_at", { ascending: false });

        if (qrError) throw qrError;
        setQrCodes(qrData as QrCodeData[]);

    } catch (error: unknown) {
      console.error("Error fetching business details:", error);
      toast.error("Failed to load business details", {
        description: error instanceof Error ? error.message : "Please try again",
        businessName: business?.name || "System"
      });
    } finally {
      setLoading(false);
    }
    };

    fetchBusinessDetails();
  }, [businessId, user, navigate]);

  const createNewQrCode = async () => {
    if (!business || !newQrName.trim()) return;
    
    try {
      const uniquePath = `${Date.now()}-${business.id.substring(0, 8)}`;
      const { data, error } = await supabase
        .from("qr_codes")
        .insert({
          business_id: business.id,
          name: newQrName.trim(),
          unique_url_path: uniquePath,
        })
        .select()
        .single();
      
      if (error) throw error;
      
      setQrCodes([data as QrCodeData, ...qrCodes]);
      toast.success("QR Code created successfully!", { businessName: business.name });
      setNewQrName("");
      setDialogOpen(false);
    } catch (error: Error) {
      console.error("Error creating QR code:", error);
      toast.error("Failed to create QR code", {
        description: error.message || "Please try again",
        businessName: business.name
      });
    }
  };

  const downloadQrCode = (qrId: string) => {
    const svg = document.getElementById(qrId);
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL("image/png");
      
      // Download PNG
      const downloadLink = document.createElement("a");
      downloadLink.download = `qrcode-${qrId}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-12 flex justify-center items-center">
          <p>Loading business details...</p>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-12">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <h1 className="text-2xl font-bold mb-4">Business Not Found</h1>
            <p className="mb-6">The business you're looking for doesn't exist or you don't have access to it.</p>
            <Button onClick={() => navigate("/dashboard")}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const getReviewUrl = (qrCode: QrCodeData) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/submit-review?code=${qrCode.unique_url_path}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold">{business.name}</h1>
              <p className="text-gray-500 mt-1">{business.description}</p>
            </div>
            <Button 
              variant="outline"
              className="mt-4 md:mt-0 flex items-center gap-2"
              onClick={() => navigate(`/edit-business/${business.id}`)}
            >
              <Edit size={16} />
              Edit Business
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="flex items-start gap-3">
              <MapPin className="text-gray-400 mt-0.5" />
              <div>
                <h3 className="font-medium text-sm">Address</h3>
                <p>{business.address}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Phone className="text-gray-400 mt-0.5" />
              <div>
                <h3 className="font-medium text-sm">Phone</h3>
                <p>{business.phone || "Not specified"}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Globe className="text-gray-400 mt-0.5" />
              <div>
                <h3 className="font-medium text-sm">Website</h3>
                <p>{business.website || "Not specified"}</p>
              </div>
            </div>
          </div>

          <Tabs defaultValue="qr-codes">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="qr-codes">QR Codes</TabsTrigger>
              <TabsTrigger value="reviews">Reviews</TabsTrigger>
            </TabsList>
            
            <TabsContent value="qr-codes">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">QR Codes</h2>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="flex items-center gap-2">
                      <Plus size={16} />
                      Create New QR Code
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New QR Code</DialogTitle>
                      <DialogDescription>
                        Create a new QR code for your customers to scan and leave reviews
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="space-y-2">
                        <label htmlFor="qr-name" className="text-sm font-medium">QR Code Name</label>
                        <Input
                          id="qr-name"
                          placeholder="Table 1, Front Door, etc."
                          value={newQrName}
                          onChange={(e) => setNewQrName(e.target.value)}
                        />
                        <p className="text-xs text-gray-500">
                          Use a descriptive name to identify this QR code's location
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                      <Button onClick={createNewQrCode} disabled={!newQrName.trim()}>
                        Create QR Code
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {qrCodes.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                  <QrCode className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium">No QR Codes Yet</h3>
                  <p className="mt-1 text-gray-500">Create your first QR code to start collecting reviews</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {qrCodes.map((qrCode) => (
                    <Card key={qrCode.id} className="overflow-hidden">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                        <CardTitle className="text-sm font-medium">{qrCode.name}</CardTitle>
                        <CardDescription>
                          Created: {new Date(qrCode.created_at).toLocaleDateString()}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="flex justify-center py-6 bg-gray-50">
                        <div className="p-4 bg-white rounded-lg">
                          <QRCode
                            id={qrCode.id}
                            value={getReviewUrl(qrCode)}
                            size={150}
                            level="M"
                            className="h-auto max-w-full"
                          />
                        </div>
                      </CardContent>
                      <CardFooter className="bg-gray-50 pt-0 pb-4 flex justify-center">
                        <Button 
                          variant="outline" 
                          className="flex items-center gap-2"
                          onClick={() => downloadQrCode(qrCode.id)}
                        >
                          <Download size={16} />
                          Download QR Code
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="reviews">
              <div className="py-6 text-center">
                <h3 className="text-lg font-medium">Reviews Module</h3>
                <p className="text-gray-500 mt-1">
                  Reviews for your business will appear here as customers submit them
                </p>
                <div className="mt-6">
                  <Button onClick={() => navigate("/dashboard")}>
                    View All Reviews in Dashboard
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default BusinessDetails;
